/* eslint-disable @typescript-eslint/no-explicit-any */
import './App.css'
import '@mantine/core/styles.css';
import '@fontsource-variable/geist'; // Loads the variable font file and @font-face
import { createTheme, MantineProvider } from '@mantine/core';
import { createBrowserRouter, createRoutesFromElements, Navigate, Route, RouterProvider, useLocation } from 'react-router-dom';
import { LandingPage } from './pages/LandingPage/LandingPage';
import { initializeApp } from "firebase/app";
import { getAuth } from "firebase/auth";
import { ForbiddenPage } from './pages/ForbiddenPage/ForbiddenPage';
import { useAppSelector } from './redux/hooks/hooks';
import { ContactPage } from './pages/Contact/ContactPage';
import { AboutPage } from './pages/AboutPage/AboutPage';
import { NotFoundPage } from './pages/NotFoundPage/NotFoundPage';
import { MemberBenefitsPage } from './pages/MemberBenefitsPage/MemberBenefitsPage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage/PrivacyPolicyPage';
import { TermsOfServicePage } from './pages/TermsOfServicePage/TermsOfServicePage';

// Our firebase project config
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Firebase Authentication and get a reference to the service
const auth = getAuth(app);

const theme = createTheme({
  fontFamily: "Geist Variable, sans-serif"
});

interface ProtectedRouteProps {
  children: React.ReactElement;
}

export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const currentUser = useAppSelector(state => state.currentUser.currentUser);
  const location = useLocation();

  if (currentUser === null) {
    // If trying to access /profile, redirect to login instead of forbidden
    if (location.pathname === "/profile") {
      return <Navigate to="/login" replace />;
    }
    // For other protected routes, you can still use forbidden if you want
    return <Navigate to="/forbidden" replace />;
  }

  return children;
};

export const ProtectedProfileRoute = ({ children }: ProtectedRouteProps) => {
  const currentUser = useAppSelector((state: any) => state.currentUser.currentUser);
  
  if (currentUser === null) {
    return <Navigate to="/login" replace />;
  }

  return children;
};


// AdminRoute.tsx
export const AdminRoute = ({ children }: ProtectedRouteProps) => {
  const orderAdminAccessGranted = useAppSelector((state: any) => 
    state.orderAdminAccess.accessLevel.accessGranted
  );
  
  if (!orderAdminAccessGranted) {
    return <Navigate to="/forbidden" replace />;
  }

  return children;
};

function App() {
  const accessGranted = useAppSelector((state : any) => state.betaAccess.accessLevel.accessGranted);

  const router = createBrowserRouter(
    createRoutesFromElements(
      <Route>
        {/* Public Routes */}
        <Route path="/" element={<LandingPage/>} />
        <Route path="/forbidden" element={<ForbiddenPage/>} />
        {/* <Route path="/search-products-list" element={<SearchProductsListPage/> } /> */}
        {/* <Route path="/product-main" element={<ProductMainPage/> } /> */}
        {/* <Route path="/cart" element={<CartPage/> } /> */}
        {/* <Route path="/login" element={<LoginPage/> } /> */}
        {/* <Route path="/signup" element={<SignupPage/> } /> */}
        {/* <Route path="/pricing" element={<PricingPage/> } /> */}
        <Route path="/contact" element={<ContactPage/> } />
        {/* <Route path="/for-contractors" element={<ContractorsPage/> } /> */}
        {/* <Route path="/order-admin-access" element={<OrderAdminAccessPage/> } />      */}
        <Route path="/about" element={<AboutPage/> } />
        <Route path="/member-benefits" element={<MemberBenefitsPage/> } />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage/> } />
        <Route path="/terms-of-service" element={<TermsOfServicePage/> } />
        {/* <Route path="/for-suppliers" element={<SupplierPage/> } /> */}

        {/* Protected Routes */}
        {/* <Route path="/profile" element={
          <ProtectedRoute><ProfilePage/></ProtectedRoute> 
        } /> */}
        {/* <Route path="/your-orders" element={
          <ProtectedRoute><YourOrdersPage/></ProtectedRoute> 
        } />
        <Route path="/checkout" element={
          <ProtectedRoute><CheckoutPage/></ProtectedRoute> 
        } />
        <Route path="/checkout-premium" element={
          <ProtectedRoute><CheckoutSubscriptionPage/></ProtectedRoute> 
        } />
        <Route path="/cancel-subscription" element={
          <ProtectedRoute><CancelSubscriptionPage/></ProtectedRoute> 
        } />
        <Route path="/purchase-subscription-success" element={
          <ProtectedRoute><PurchaseSubscriptionSuccessPage/></ProtectedRoute> 
        } />
        <Route path="/purchase-success" element={
          <ProtectedRoute><PurchaseSuccessPage/></ProtectedRoute> 
        } />
        <Route path="/cancel-subscription-success" element={
          <ProtectedRoute><CancelSubscriptionSuccessPage/></ProtectedRoute> 
        } /> */}
        {/* Admin Routes */}
        {/* <Route path="/order-admin" element={
          <AdminRoute><OrderAdminPage/></AdminRoute> 
        } />
        {/* 404 Not Found */}
        <Route path="/*" element={<NotFoundPage/>} />
      </Route>
    )
  );

  return (
      <MantineProvider theme={theme}>
        <RouterProvider router={router} />
      </MantineProvider>
  );
}

export default App
